import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  ChipGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  TextArea,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { REJECTION_REASONS, ADMIN_LOAN_DETAIL_KEY } from './constants';
import { RejectApplicationModalProps } from './types';
import { ACTION_MODAL_Z_INDEX } from 'components/SalaryStructures/creation/components/styles';

export const RejectApplicationModal = ({
  isOpen,
  onDismiss,
  loanId,
  onSuccess,
}: RejectApplicationModalProps) => {
  const [remarks, setRemarks] = useState('');
  const [selectedChip, setSelectedChip] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setRemarks('');
      setSelectedChip(null);
    }
  }, [isOpen]);

  const rejectMutation = useMutation({
    mutationFn: (rejectRemarks: string) =>
      api.loans.updateLoanStatus(loanId!, { status: 'loan-rejected', remarks: rejectRemarks }),
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      queryClient.invalidateQueries([ADMIN_LOAN_DETAIL_KEY, loanId]);
      onDismiss();
      onSuccess?.();
      showToast({ content: 'Request successfully rejected.', color: 'positive', duration: 4000 });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to reject loan. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleTextChange = (value: string) => {
    setRemarks(value);
    setSelectedChip(REJECTION_REASONS.includes(value) ? value : null);
  };

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="small" zIndex={ACTION_MODAL_Z_INDEX}>
      <ModalHeader title="Reject Application" />
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.5">
          <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
            Add a relevant reason to reject this application
          </Text>

          <TextArea
            label="Reason"
            necessityIndicator="required"
            placeholder="Provide a reason to reject this application"
            value={remarks}
            onChange={({ value }) => handleTextChange(value ?? '')}
          />

          <ChipGroup
            accessibilityLabel="Rejection reason"
            selectionType="single"
            value={selectedChip ?? undefined}
            onChange={({ values }) => {
              const selected = values[0] ?? null;
              setSelectedChip(selected);
              setRemarks(selected ?? '');
            }}>
            {REJECTION_REASONS.map((reason) => (
              <Chip key={reason} value={reason}>
                {reason}
              </Chip>
            ))}
          </ChipGroup>
        </Box>
      </ModalBody>

      <ModalFooter>
        <Box display="flex" gap="spacing.4" width="100%">
          <Button variant="secondary" isFullWidth onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            color="negative"
            isFullWidth
            isLoading={rejectMutation.isLoading}
            isDisabled={!remarks.trim()}
            onClick={() => rejectMutation.mutate(remarks.trim())}>
            Reject
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
